"use client"

import Link from "next/link"
import { Bell, LayoutDashboard, CalendarCheck, Loader2, ArrowRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { buildTimelineUrl } from "@/lib/timeline-url"
import type { AnalysisResult } from "@/hooks/use-homepage-analysis"

interface ResultQuickLinksProps {
  analysisResult: AnalysisResult
  zipCode: string
  uploadedImageUrl: string | null
  savedAnalysisId: string | null
  isSubmitting: boolean
  onSaveAndViewTimeline: () => void
  onViewTimeline: () => void
  className?: string
}

export function ResultQuickLinks({
  analysisResult,
  zipCode,
  uploadedImageUrl,
  savedAnalysisId,
  isSubmitting,
  onSaveAndViewTimeline,
  onViewTimeline,
  className,
}: ResultQuickLinksProps) {
  const timelineHref = buildTimelineUrl({
    zipCode,
    grassType: analysisResult.grassType,
    savedAnalysisId,
    uploadedImageUrl,
  })

  const alreadySaved = Boolean(savedAnalysisId)

  return (
    <div
      id="care-plan-upgrade"
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 md:p-7 scroll-mt-24 shadow-sm",
        className,
      )}
    >
      <div className="mb-4 text-center">
        <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mb-1.5">
          You're signed in
        </div>
        <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900 leading-tight">
          Your full plan, ready when you are
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-xl mx-auto mb-4">
        {alreadySaved ? (
          <Button asChild className="bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-xl font-semibold text-[13.5px] shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-200 min-h-[44px]">
            <Link href={timelineHref} onClick={onViewTimeline}>
              <CalendarCheck className="w-4 h-4 mr-1.5" />
              See 12-month plan
            </Link>
          </Button>
        ) : (
          <Button
            onClick={onSaveAndViewTimeline}
            disabled={isSubmitting}
            className="bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-xl font-semibold text-[13.5px] shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CalendarCheck className="w-4 h-4 mr-1.5" />
                See 12-month plan
              </>
            )}
          </Button>
        )}

        <Link
          href="/dashboard/reminders"
          className="inline-flex items-center justify-center gap-1.5 bg-white border-2 border-green-200 text-green-800 hover:border-green-300 hover:bg-green-50 px-4 py-3 rounded-xl font-semibold text-[13.5px] transition-all duration-200 min-h-[44px]"
        >
          <Bell className="w-4 h-4" />
          Reminders
        </Link>

        <Link
          href="/dashboard"
          className="inline-flex items-center justify-center gap-1.5 bg-white border-2 border-green-200 text-green-800 hover:border-green-300 hover:bg-green-50 px-4 py-3 rounded-xl font-semibold text-[13.5px] transition-all duration-200 min-h-[44px]"
        >
          <LayoutDashboard className="w-4 h-4" />
          My dashboard
        </Link>
      </div>

      <p className="text-[12px] text-gray-500 text-center max-w-md mx-auto">
        This lawn is saved to your account. Reminders fire monthly based on your ZIP.
      </p>

      <div className="mt-3 text-center">
        <Link
          href={timelineHref}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-green-700 hover:text-green-800 transition-colors"
        >
          Jump to full timeline
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  )
}
